import React, { useState, useRef, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { gtmEventHandler } from '@common/utils/gtm';
import { View, Text, Icon } from '@vectord/ui';

import { useMediaQuery } from '@common/hooks';

import { fcSelector } from '@alumni_v2/selectors/reviews';
import { closeFCModal, openFCModal } from '@alumni_v2/stores/reviews/actions';
import {
  FC_DSML_THUMBNAIL, FC_ACADEMY_THUMBNAIL,
  FC_DSML_VIDEO_LINK, FC_ACADEMY_VIDEO_LINK,
} from '@alumni_v2/utils/constants';

import FcRegisterButton from '../FcRegisterButton';
import VideoModal from '../VideoModal';


const FCBanner = () => {
  const { tablet } = useMediaQuery();

  const dispatch = useDispatch();
  const {
    isFcRegistered,
  } = useSelector(fcSelector);
  const product = useSelector(state => state.reviews.product) || 'academy';

  const [videoThumbnail, setVideoThumbnail] = useState(null);
  const [videoUrl, setVideoUrl] = useState(null);

  const videoRef = useRef();

  useEffect(() => {
    gtmEventHandler(
      'cta',
      'free_class_banner_view',
      'click',
      'alumni_directory',
    );
  }, []);

  useEffect(() => {
    if (product === 'data_science') {
      setVideoUrl(FC_DSML_VIDEO_LINK);
      setVideoThumbnail(FC_DSML_THUMBNAIL);
    } else {
      setVideoUrl(FC_ACADEMY_VIDEO_LINK);
      setVideoThumbnail(FC_ACADEMY_THUMBNAIL);
    }
  }, [product]);

  const handlePlayClick = () => {
    gtmEventHandler(
      'cta',
      'free-class-banner-video-modal-open',
      'click',
      'alumni_directory',
    );

    if (!videoUrl) {
      if (product === 'data_science') {
        setVideoUrl(FC_DSML_VIDEO_LINK);
      } else {
        setVideoUrl(FC_ACADEMY_VIDEO_LINK);
      }
    }
    dispatch(openFCModal());

    if (videoRef.current) {
      videoRef.current.play();

      gtmEventHandler(
        'cta',
        'free-class-banner-video-play',
        'click',
        'alumni_directory',
      );
    }
  };

  const handleCloseModal = () => {
    gtmEventHandler(
      'cta',
      'free-class-banner-video-modal-close',
      'click',
      'alumni_directory',
    );

    const video = videoRef.current;
    if (video) {
      video.pause();

      gtmEventHandler(
        'cta',
        'free-class-banner-video-pause',
        'click',
        'alumni_directory',
      );
    }
    setVideoUrl(null);
    dispatch(closeFCModal());
  };

  return (
    <>
      <View className="vd-flex-col vd-my-12">
        <View className="vd-bg-surface-neutral-inverse-base vd-px-24
          sm:vd-px-48 vd-py-24 vd-justify-between
          vd-flex-col-reverse sm:vd-flex-row vd-gap-y-12"
        >
          <View className="vd-flex-col vd-justify-center
            vd-w-full sm:vd-w-[70%] sm:vd-mr-56"
          >
            {isFcRegistered && (
              <View className="vd-flex vd-gap-8 vd-items-center vd-mb-12">
                <Icon
                  color="icon-neutral-inverse-strong"
                  iconSize="xxs"
                  name="circle-wavy-check"
                  type="fill"
                />
                <Text
                  color="text-neutral-inverse-tertiary"
                  fontFamily="sans"
                  fontSize="b3"
                  fontStyle="roman"
                  letterSpacing="wide"
                  weight="bold"
                >
                  Free Class Successfully Registered
                </Text>
              </View>
            )}

            <View className="vd-gap-y-8 vd-flex-col vd-items-start">
              <Text
                color="text-neutral-inverse-primary"
                fontSize="h2"
                weight="bold"
              >
                Learn what lead to these outcomes from Scaler Founder
              </Text>
              <Text
                color="text-neutral-inverse-tertiary"
                fontSize="b3"
                weight="regular"
              >
                Join Anshuman Singh, Co-Founder at Scaler and ex
                tech lead at FB, to explore
                how Scaler can make a difference for you
              </Text>
            </View>
            <View className="vd-flex-col sm:vd-flex-row vd-gap-x-28 vd-mt-28
              vd-flex-row sm:vd-items-center vd-w-full
              vd-gap-y-16 vd-items-start"
            >
              <FcRegisterButton source="banner" />
              {!isFcRegistered && !tablet && (
                <View className="vd-gap-x-8 vd-hidden
                  sm:vd-flex vd-self-center"
                >
                  <Icon
                    color="icon-alert-caution-soft"
                    iconSize="sm"
                    name="users"
                    type="fill"
                  />
                  <Text
                    color="text-neutral-inverse-tertiary"
                    fontSize="b3"
                  >
                    Seats Filling Fast
                  </Text>
                </View>
              )}
            </View>
          </View>
          <View
            className="vd-relative vd-flex vd-items-center
              vd-justify-center vd-w-full sm:vd-w-640"
            onClick={handlePlayClick}
          >
            <View
              className="vd-px-24
                vd-py-24 vd-bg-surface-neutral-base
                vd-absolute vd-top-[50%] vd-left-[50%]
                vd--translate-y-2/4 vd--translate-x-2/4 vd-cursor-pointer"
            >
              <Icon name="play" type="fill" />
            </View>
            <img
              src={videoThumbnail}
              alt="Video Thumbnail"
              className="vd-max-fit sm:vd-max-w-[392px]"
            />
          </View>
        </View>
      </View>
      <VideoModal
        onClose={handleCloseModal}
        videoLink={videoUrl}
        videoRef={videoRef}
      />
    </>
  );
};

export default FCBanner;
